import React, {useMemo} from 'react';
import classNames from 'classnames';
import {IntlShape} from 'react-intl';
import {Lx} from '../../../../i18n/consts';
import {SVGS} from '../../../../assets/images';
import StateIcon from '../../timesheet-state-icon';
import {
    ActionIconButton,
    HoursValue,
    formatHours
} from '../../../../pages/Schedule/components/timesheet-grid/components/cells';
import TimesheetActionsMenu from '../../../../pages/Schedule/components/timesheet-grid/components/actions-menu';
import styles from '../../../../pages/Schedule/components/timesheet-grid/grid.module.scss';
import {TimeSheetItemStatus, UpdateStatusAbility} from '../../../../features/schedule/types';
import {PopupMenuAction} from '../../../../components/shared/popup-menu/types';

interface Props {
    name: string,
    total: number,
    approved: number,
    approvableHours: number;
    billed: number,
    approvable: number;
    billable: number;
    billableHours?: number;
    intl: IntlShape;
    onApproveAllClick: () => Promise<void>;
    onBillAllClick: () => Promise<void>;
    onDisapproveAllClick: () => Promise<void>;
    onCancelAllBillingClick: () => Promise<void>;
    onAddNewShiftClick: () => Promise<void>;
    approveAbility: UpdateStatusAbility,
    billalbeAbility: UpdateStatusAbility;
    hideAddAction?: boolean;
    hideButtons?: boolean;
    hideTrackingHours?: boolean;
    workHours?: number;
    absenceHours?: number;
    plannedHours?: number;
    templateColumns?: string;
    summaryColumnIndexes?: Record<string, number>,
    collapseColumnEnd?: number,
}

const DataRowHeader: React.FC<Props> = (props) => {
    const totalHours = props.total ?? 0;
    const workHours = props.workHours ?? 0;
    const billableHours = props.billableHours ?? workHours;
    const absenceHours = props.absenceHours ?? 0;
    const plannedHours = props.plannedHours ?? 0;
    const approvedValue = props.approved ?? 0;
    const approvableHours = props.approvableHours ?? 0;
    const billedValue = props.billed ?? 0;
    const showTracking = !props.hideTrackingHours;
    const shouldShowPlannedHours = showTracking && props.summaryColumnIndexes?.plannedHours !== undefined;
    const shouldShowWorkHours = showTracking && props.summaryColumnIndexes?.workHours !== undefined;
    const shouldShowAbsenceHours = showTracking && props.summaryColumnIndexes?.absenceHours !== undefined;

    const approveActive = approvableHours > 0 && approvedValue >= approvableHours;
    const billableActive = billableHours > 0 && billedValue >= billableHours;
    const canApprove = props.approveAbility ? (props.approveAbility.canApply || props.approveAbility.canCancel) : true;
    const canBill = props.billalbeAbility ? (props.billalbeAbility.canApply || props.billalbeAbility.canCancel) : true;

    const approveDisabled = props.hideButtons
        || approvableHours < 1
        || (props.approveAbility
            ? approveActive
                ? !props.approveAbility.canCancel
                : !props.approveAbility.canApply
            : false);

    const billableDisabled = props.hideButtons
        || billableHours < 1
        || (props.billalbeAbility
            ? billableActive
                ? !props.billalbeAbility.canCancel
                : !props.billalbeAbility.canApply
            : false);

    const approveLabel = useMemo(() => approveActive
        ? props.intl.formatMessage({ id: Lx.Schedule.DISAPPROVE_FORMAT_ALL }, { s: formatHours(approvedValue) })
        : props.intl.formatMessage({ id: Lx.Schedule.APPROVE_ALL_FORMAT }, { s: formatHours(props.approvable) }), [
        approveActive,
        approvedValue,
        props.approvable,
        props.intl
    ]);

    const billableLabel = useMemo(() => billableActive
        ? props.intl.formatMessage({ id: Lx.Schedule.CANCEL_BILL_FORMAT }, { s: formatHours(billedValue) })
        : props.intl.formatMessage({ id: Lx.Schedule.BILL_ALL_FORMAT }, { s: formatHours(props.billable) }), [
        billableActive,
        billedValue,
        props.billable,
        props.intl
    ]);

    const addLabel = useMemo(
        () => props.intl.formatMessage({ id: Lx.Schedule.CREATE_NEW_TIMESHIFT_SHIFT }),
        [props.intl]
    );

    const templateStyle = props.templateColumns
        ? { gridTemplateColumns: props.templateColumns }
        : undefined;
    const nameColumnStyle = props.collapseColumnEnd
        ? { gridColumn: `1 / ${props.collapseColumnEnd}` }
        : undefined;
    const buildColumnStyle = (field: string) => {
        const index = props.summaryColumnIndexes?.[field];
        if (index === undefined) {
            return undefined;
        }

        const start = index + 1;
        return { gridColumn: `${start} / ${start + 1}` };
    };

    const handleApproveClick = () => {
        if (approveActive) {
            return props.onDisapproveAllClick();
        }

        return props.onApproveAllClick();
    };

    const handleBillableClick = () => {
        if (billableActive) {
            return props.onCancelAllBillingClick();
        }

        return props.onBillAllClick();
    };

    const menuActions = useMemo<PopupMenuAction[]>(() => {
        const actions: PopupMenuAction[] = [];

        if (canApprove) {
            actions.push({
                name: approveLabel,
                icon: (
                    <StateIcon
                        status={approveActive ? TimeSheetItemStatus.APPROVED : TimeSheetItemStatus.NORMAL}
                        active={approveActive}
                    />
                ),
                callback: handleApproveClick,
                disabled: approveDisabled || props.hideButtons
            });
        }

        if (canBill) {
            actions.push({
                name: billableLabel,
                icon: <StateIcon status={TimeSheetItemStatus.BILLABLE} active={billableActive} />,
                callback: handleBillableClick,
                disabled: billableDisabled || props.hideButtons
            });
        }

        if (!props.hideAddAction) {
            actions.push({
                name: addLabel,
                icon: <SVGS.PlusIcon />,
                callback: props.onAddNewShiftClick,
                disabled: props.hideButtons
            });
        }

        return actions;
    }, [
        addLabel,
        approveActive,
        approveDisabled,
        billableActive,
        billableDisabled,
        billableLabel,
        canApprove,
        canBill,
        handleApproveClick,
        handleBillableClick,
        props.hideAddAction,
        props.hideButtons,
        props.onAddNewShiftClick,
        approveLabel
    ]);

    return (
        <div className={styles.groupRow} style={templateStyle}>
            <div
                className={classNames(styles.groupCell, styles.groupName)}
                title={props.name}
                style={nameColumnStyle}
            >
                {props.name}
            </div>
            {shouldShowPlannedHours && (
                <div
                    className={classNames(styles.groupCell, styles.metricCell, styles.metricCellRegular)}
                    style={buildColumnStyle('plannedHours')}
                >
                    <HoursValue value={plannedHours} regularWeight />
                </div>
            )}
            {shouldShowWorkHours && (
                <div
                    className={classNames(styles.groupCell, styles.metricCell, styles.metricCellRegular)}
                    style={buildColumnStyle('workHours')}
                >
                    <HoursValue value={workHours} regularWeight />
                </div>
            )}
            {shouldShowAbsenceHours && (
                <div
                    className={classNames(styles.groupCell, styles.metricCell)}
                    style={buildColumnStyle('absenceHours')}
                >
                    <HoursValue value={absenceHours} type='absence' />
                </div>
            )}
            <div
                className={classNames(styles.groupCell, styles.metricCell)}
                style={buildColumnStyle('approvedHours')}
            >
                <HoursValue
                    value={approvedValue}
                    total={approvableHours}
                    type='approve'
                    mutedZero
                />
            </div>
            <div
                className={classNames(styles.groupCell, styles.metricCell)}
                style={buildColumnStyle('billableHours')}
            >
                <HoursValue
                    value={billedValue}
                    total={billableHours}
                    type='billable'
                    mutedZero
                />
            </div>
            <div
                className={classNames(styles.groupCell, styles.metricCell)}
                style={buildColumnStyle('totalHours')}
            >
                <HoursValue value={totalHours} />
            </div>
            <div
                className={classNames(styles.groupCell, styles.actionCell)}
                style={buildColumnStyle('approveAction')}
            >
                <ActionIconButton
                    icon={
                        <StateIcon
                            status={approveActive ? TimeSheetItemStatus.APPROVED : TimeSheetItemStatus.NORMAL}
                            active={approveActive}
                        />
                    }
                    label={approveLabel}
                    disabled={approveDisabled}
                    onClick={props.hideButtons ? undefined : handleApproveClick}
                    variant='approve'
                    active={approveActive}
                />
            </div>
            <div
                className={classNames(styles.groupCell, styles.actionCell)}
                style={buildColumnStyle('billableAction')}
            >
                <ActionIconButton
                    icon={<StateIcon status={TimeSheetItemStatus.BILLABLE} active={billableActive} />}
                    label={billableLabel}
                    disabled={billableDisabled}
                    onClick={props.hideButtons ? undefined : handleBillableClick}
                    variant='billable'
                    active={billableActive}
                />
            </div>
            <div
                className={classNames(styles.groupCell, styles.actionCell)}
                style={buildColumnStyle('actions')}
            >
                <TimesheetActionsMenu actions={menuActions} disabled={props.hideButtons} />
            </div>
        </div>
    );
};

export default DataRowHeader
